import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../lib/AuthContext';
import { toast } from 'sonner';
import { Upload, TrendingUp, Users, ChevronDown, ChevronUp, ArrowRight, Trophy, AlarmClock, ShieldAlert } from 'lucide-react';
import DateRangePicker from '../components/DateRangePicker';
import MonthlyAppointmentsBar from '../components/MonthlyAppointmentsBar';

// Convert a DateRangePicker { from, to } value → { start, months } that
// /api/performance/report understands.
function rangeToMonths(range) {
  const today = new Date();
  const to = range?.to ? new Date(range.to) : today;
  const from = range?.from ? new Date(range.from) : new Date(today.getFullYear(), 0, 1);
  const start = `${from.getFullYear()}-${String(from.getMonth() + 1).padStart(2, '0')}`;
  const months = Math.max(1, (to.getFullYear() - from.getFullYear()) * 12 + (to.getMonth() - from.getMonth()) + 1);
  return { start, months };
}
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell as ReCell } from 'recharts';

const GREEN = 'bg-[#C6EFCE] text-[#839E97]';
const AMBER = 'bg-[#F7EFDA] text-[#B08A3A]';
const RED = 'bg-[#F5EAEA] text-[#8A3A3A]';
const GRAY = 'bg-[#E8E5EC] text-[#685B72]';
const OK_DOT = '#839E97', WARN_DOT = '#B08A3A', BAD_DOT = '#8A3A3A', OFF_DOT = '#D8D6E2';

function fmtPct(v) { return v == null ? '—' : (v * 100).toFixed(1) + '%'; }
function fmtNum(v) { return v == null ? '' : Math.round(v).toLocaleString(); }
function fmtFloat(v, d = 1) { return v == null ? '' : v.toFixed(d); }
function rateColor(v, thr) { if (v == null) return ''; return v >= thr.green ? GREEN : v >= thr.yellow ? AMBER : RED; }
function rateDot(v, thr) { if (v == null) return OFF_DOT; return v >= thr.green ? OK_DOT : v >= thr.yellow ? WARN_DOT : BAD_DOT; }
function countColor(v, thr) { if (v == null) return ''; return v > thr.count ? RED : ''; }

// ────────────────────────────── Targets modal ──────────────────────────────
function TargetsPanel({ open, onClose, onSaved }) {
  const [rows, setRows] = useState([]);       // {name, weekly_target}
  const [drafts, setDrafts] = useState({});   // {name: newValue}
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState('');
  const [effective, setEffective] = useState(() => new Date().toISOString().slice(0, 7));

  const reload = useCallback(async () => {
    const r = await api.get('/performance/clinicians');
    setRows(r.data.clinicians || []);
    setDrafts({});
  }, []);

  useEffect(() => { if (open) reload(); }, [open, reload]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r => r.name.toLowerCase().includes(q));
  }, [rows, filter]);

  const saveAll = async () => {
    const updates = {};
    for (const [n, v] of Object.entries(drafts)) {
      if (v === '' || v == null) updates[n] = null;
      else if (!Number.isNaN(Number(v))) updates[n] = Number(v);
    }
    if (!Object.keys(updates).length) { toast.info('No changes to save'); return; }
    setBusy(true);
    try {
      await api.put('/performance/targets', { targets: updates, effective_from: effective });
      toast.success(`Updated ${Object.keys(updates).length} target(s) effective ${effective}`);
      await reload();
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    setBusy(false);
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <Card className="max-w-3xl w-full max-h-[85vh] overflow-hidden p-0 rounded-2xl bg-white flex flex-col" onClick={e => e.stopPropagation()} data-testid="targets-panel">
        <div className="px-6 py-4 border-b border-[#E8E5EC] flex items-center justify-between">
          <div>
            <h3 className="font-serif text-xl text-[#2D2833]">Clinician weekly targets</h3>
            <p className="text-xs text-[#685B72] mt-0.5">Set the target number of billable sessions per week for each clinician. Blank = no target (row shows as “—”).</p>
          </div>
          <button onClick={onClose} className="text-[#685B72] text-lg" data-testid="targets-close">✕</button>
        </div>
        <div className="px-6 py-3 border-b border-[#E8E5EC] flex items-center gap-3 flex-wrap">
          <Input placeholder="Filter by clinician name…" value={filter} onChange={e => setFilter(e.target.value)} className="rounded-xl flex-1 min-w-[180px]" data-testid="targets-filter" />
          <label className="text-xs text-[#685B72] whitespace-nowrap">Effective from</label>
          <Input type="month" value={effective} onChange={e => setEffective(e.target.value)} className="rounded-xl w-40" data-testid="targets-effective" />
          <div className="text-xs text-[#685B72] whitespace-nowrap">{filtered.length}/{rows.length}</div>
        </div>
        <div className="flex-1 overflow-auto px-6 py-3">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-white">
              <tr className="text-[#685B72] text-left border-b border-[#E8E5EC]">
                <th className="p-2">Clinician</th>
                <th className="p-2 w-32">Current</th>
                <th className="p-2 w-40">New target / wk</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => {
                const draft = drafts[r.name];
                return (
                  <tr key={r.name} className="border-b border-[#F1EDF5]" data-testid={`tgt-row-${r.name.replace(/\W+/g, '_')}`}>
                    <td className="p-2">{r.name}</td>
                    <td className="p-2 font-mono text-[#685B72]">{r.weekly_target ?? '—'}</td>
                    <td className="p-2">
                      <Input
                        type="number"
                        step="0.5"
                        placeholder={r.weekly_target != null ? String(r.weekly_target) : '—'}
                        value={draft ?? ''}
                        onChange={e => setDrafts(d => ({ ...d, [r.name]: e.target.value }))}
                        className="rounded-lg h-8 w-32"
                        data-testid={`tgt-input-${r.name.replace(/\W+/g, '_')}`}
                      />
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && <tr><td colSpan={3} className="p-6 text-center text-[#685B72]">No clinicians match this filter. Upload attendance data or add clinicians in <Link to="/clinicians" className="underline">Clinician Roster</Link>.</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="px-6 py-3 border-t border-[#E8E5EC] flex items-center justify-between">
          <div className="text-xs text-[#685B72]">
            To add or remove clinicians, use <Link to="/clinicians" className="underline">Clinician Roster</Link>.
            Clinicians with no session data for 3+ months are auto-hidden from reports.
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} className="rounded-xl" data-testid="targets-cancel">Close</Button>
            <Button onClick={saveAll} disabled={busy} className="bg-[#685B72] text-white rounded-xl" data-testid="targets-save">
              {busy ? 'Saving…' : `Save ${Object.keys(drafts).length} change${Object.keys(drafts).length === 1 ? '' : 's'}`}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}

// ────────────────────────────── Uploads ──────────────────────────────
function UploadRow({ label, endpoint, onDone }) {
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState('');
  const submit = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true); setStatus('');
    try {
      const fd = new FormData(); fd.append('file', f);
      const r = await api.post(endpoint, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const rows = r.data.rows_parsed ?? 0;
      const monthsInfo = r.data.months_touched?.length ? `${r.data.months_touched.length} months` : (r.data.effective_from ? `effective ${r.data.effective_from}` : '');
      setStatus(`✓ ${rows} rows${monthsInfo ? ' · ' + monthsInfo : ''}`);
      toast.success(`${label}: ${rows.toLocaleString()} rows${monthsInfo ? ' · ' + monthsInfo : ''}`);
      onDone?.();
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Upload failed';
      setStatus(`✗ ${detail}`);
      toast.error(detail);
    }
    setBusy(false); e.target.value = '';
  };
  return (
    <label className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl border cursor-pointer text-sm ${busy ? 'opacity-50 pointer-events-none border-[#E8E5EC]' : 'border-[#BAA9CA] hover:bg-[#F4F2F6]'}`}>
      <Upload size={14} /> {busy ? 'Uploading…' : label}
      {status && <span className="text-[10px] text-[#685B72] ml-1">{status}</span>}
      <input type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={submit} data-testid={`upload-${endpoint.split('/').pop()}`} />
    </label>
  );
}

// ────────────────────────────── Visual pieces ──────────────────────────────
function HeroTile({ label, value, sub, tone = 'neutral', testId }) {
  const tones = {
    ok:      'bg-[#839E97]/15 border-[#839E97]/40 text-[#839E97]',
    warn:    'bg-[#B08A3A]/15 border-[#B08A3A]/40 text-[#B08A3A]',
    bad:     'bg-[#8A3A3A]/12 border-[#8A3A3A]/40 text-[#8A3A3A]',
    accent:  'bg-[#BAA9CA]/20 border-[#BAA9CA]/50 text-[#685B72]',
    neutral: 'bg-white border-[#E8E5EC] text-[#2D2833]',
  };
  return (
    <div className={`rounded-2xl border p-4 ${tones[tone]}`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-widest opacity-80 font-semibold">{label}</div>
      <div className="font-serif text-3xl mt-1 leading-tight">{value}</div>
      {sub && <div className="text-xs opacity-75 mt-1">{sub}</div>}
    </div>
  );
}

function TrendSparkline({ data, thr }) {
  const chartData = data.map((v, i) => ({ i, v: v == null ? null : Math.round(v * 1000) / 10 }));
  return (
    <div style={{ height: 60, width: 120 }}>
      <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
        <LineChart data={chartData} margin={{ top: 2, right: 4, left: 4, bottom: 2 }}>
          <YAxis hide domain={[Math.min(60, thr.yellow * 100 - 5), 100]} />
          <Line type="monotone" dataKey="v" stroke="#685B72" strokeWidth={1.5} dot={false} isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// ────────────────────────────── Detailed table (collapsible) ──────────────────────────────
function DetailedTable({ report, scope, thr, hideByClinic, scopeSelector }) {
  const [expanded, setExpanded] = useState(false);
  if (!report) return null;
  return (
    <Card className="rounded-2xl border-[#E8E5EC]" data-testid="perf-detailed">
      <div className="w-full flex items-center justify-between p-4">
        <button
          onClick={() => setExpanded(x => !x)}
          className="flex-1 text-left hover:opacity-80 transition-opacity"
          data-testid="detailed-toggle"
        >
          <div className="font-serif text-lg text-[#2D2833]">Detailed month-by-month table</div>
          <div className="text-xs text-[#685B72]">The full spreadsheet view — every metric, every month.</div>
        </button>
        <div className="flex items-center gap-3">
          {scopeSelector}
          <button
            onClick={() => setExpanded(x => !x)}
            className="p-1 rounded hover:bg-[#F4F2F6]"
            aria-label={expanded ? 'Collapse detailed table' : 'Expand detailed table'}
            data-testid="detailed-toggle-chevron"
          >
            {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>
        </div>
      </div>
      {expanded && <DetailedTableInner report={report} scope={scope} thr={thr} hideByClinic={hideByClinic} />}
    </Card>
  );
}

function DetailedTableInner({ report, scope, thr, hideByClinic }) {
  const monthsHead = report.months || [];
  const allClinics = report.clinics || [];
  // Hide months that have no data yet — a month counts as "empty" when
  // Scheduled and Show are both null/0. Uploaded data starts populating
  // these arrays month-by-month, so as SP exports land, months surface
  // automatically without needing an explicit toggle.
  const isEmpty = (i) => {
    const s = Number(report.summary?.scheduled?.[i]) || 0;
    const sh = Number(report.summary?.show?.[i]) || 0;
    return s === 0 && sh === 0;
  };
  const visibleIdx = monthsHead.map((_, i) => i).filter(i => !isEmpty(i));
  const visibleMonths = visibleIdx.map(i => monthsHead[i]);
  const pick = (arr) => (Array.isArray(arr) ? visibleIdx.map(i => arr[i]) : []);
  // Wrap the summary + mix + by_clinic + cpt + clinicians arrays so every
  // Row / ClinicianRow renders exactly `visibleMonths.length` cells.
  const summary = Object.fromEntries(Object.entries(report.summary || {}).map(([k, v]) => [k, pick(v)]));
  const mix = Object.fromEntries(Object.entries(report.mix || {}).map(([k, v]) => [k, pick(v)]));
  const byClinic = report.by_clinic ? {
    show_rate: Object.fromEntries(Object.entries(report.by_clinic.show_rate || {}).map(([c, v]) => [c, pick(v)])),
    completed: Object.fromEntries(Object.entries(report.by_clinic.completed || {}).map(([c, v]) => [c, pick(v)])),
  } : null;
  const cpt = {
    codes: report.cpt?.codes || [],
    series: Object.fromEntries(Object.entries(report.cpt?.series || {}).map(([k, v]) => [k, pick(v)])),
  };
  const clinicians = (report.clinicians || []).map(c => ({
    ...c,
    show: pick(c.show),
    rate: pick(c.rate),
    avg_per_week: pick(c.avg_per_week),
    vs_target: pick(c.vs_target),
    cancel: pick(c.cancel),
    late: pick(c.late),
    noshow: pick(c.noshow),
    clincxl: pick(c.clincxl),
    present: pick(c.present),
  }));
  // ── Section-level month header repeat ─────────────────────────────
  return (
    <div className="p-4 border-t border-[#E8E5EC]">
      <p className="text-xs text-[#685B72] mb-3" data-testid="detailed-table-desc">
        This full-detail table shows every attendance, mix, per-clinic and per-clinician metric side-by-side for {visibleMonths.length} month{visibleMonths.length === 1 ? '' : 's'} with data. Empty months are hidden and will surface automatically as new uploads land. The month header stays pinned at the top so you always know which column you're reading.
      </p>
      <div className="overflow-auto max-h-[75vh] rounded-lg border border-[#E8E5EC]" data-testid="detailed-table-scroll">
      <table className="text-xs w-full border-collapse">
        <thead>
          <tr>
            <th className="px-1.5 py-2 text-left bg-white text-[10px] font-semibold uppercase tracking-widest text-[#4A3B5F] whitespace-nowrap sticky left-0 top-0 z-40 leading-tight w-px">
              Metric
            </th>
            {visibleMonths.map(m => (
              <th
                key={m.key}
                className="px-1.5 py-2 text-center text-white bg-[#685B72] text-[10px] font-semibold min-w-[60px] whitespace-nowrap sticky top-0 z-30 leading-tight"
              >
                {m.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <SectionRow label={scope === 'group' ? 'GROUP ATTENDANCE SUMMARY' : `${scope.toUpperCase()} ATTENDANCE SUMMARY`} />
          <Row label="Scheduled appointments" values={summary.scheduled} fmt={fmtNum} />
          <Row label="Completed (Show)" values={summary.show} fmt={fmtNum} bold rowBg="bg-[#F4F2F6]" />
          <Row label="Show rate" values={summary.show_rate} fmt={fmtPct} bold cellColor={v => rateColor(v, thr)} />
          <Row label="Client cancellations" values={summary.cancel} fmt={fmtNum} indent />
          <Row label="Late cancellations" values={summary.late} fmt={fmtNum} indent />
          <Row label="No shows" values={summary.noshow} fmt={fmtNum} indent />
          <Row label="Clinician cancellations" values={summary.clincxl} fmt={fmtNum} indent />

          <SectionRow label="ATTENDANCE MIX — % OF SCHEDULED" color="#BAA9CA" />
          {[['Show', 'show'], ['Client cancel', 'cancel'], ['Late cancel', 'late'], ['No show', 'noshow'], ['Clinician cancel', 'clincxl']].map(([lbl, k]) => (
            <Row key={k} label={lbl} values={mix[k]} fmt={fmtPct} />
          ))}

          {byClinic && !hideByClinic && (
            <>
              <SectionRow label="SHOW RATE BY CLINIC" color="#839E97" />
              {allClinics.map(c => <Row key={c} label={c} values={byClinic.show_rate[c]} fmt={fmtPct} cellColor={v => rateColor(v, thr)} />)}
              <SectionRow label="COMPLETED SESSIONS BY CLINIC" />
              {allClinics.map(c => <Row key={c} label={c} values={byClinic.completed[c]} fmt={fmtNum} />)}
            </>
          )}

          <SectionRow label={`CPT CODE MIX — ${scope === 'group' ? 'GROUP' : scope.toUpperCase()}`} color="#BAA9CA" />
          {cpt.codes.map(({ code, label }) => <Row key={code} label={label} values={cpt.series[code]} fmt={fmtPct} />)}
          <Row label="All other codes" values={cpt.series.other} fmt={fmtPct} />

          <SectionRow label="CLINICIAN — COMPLETED SESSIONS" />
          {clinicians.map(c => <ClinicianRow key={c.name} clin={c} field="show" fmt={fmtNum} />)}
          <SectionRow label="CLINICIAN — SHOW RATE" color="#839E97" />
          {clinicians.map(c => <ClinicianRow key={c.name} clin={c} field="rate" fmt={fmtPct} thr={thr} rateColored />)}
          <SectionRow label="CLINICIAN — AVG SESSIONS / WEEK (vs target)" />
          {clinicians.map(c => (
            <ClinicianRow key={c.name} clin={c} field="avg_per_week" fmt={v => fmtFloat(v)} thr={thr} colorFrom="vs_target" nameSuffix={c.target ? ` (tgt ${c.target})` : ''} />
          ))}
          <SectionRow label="CLINICIAN — % OF WEEKLY TARGET" color="#839E97" />
          {clinicians.map(c => <ClinicianRow key={c.name} clin={c} field="vs_target" fmt={fmtPct} thr={thr} rateColored />)}
          <SectionRow label="CLIENT CANCELLATIONS" />
          {clinicians.map(c => <ClinicianRow key={c.name} clin={c} field="cancel" fmt={fmtNum} thr={thr} countColored />)}
          <SectionRow label={`LATE CANCELLATIONS (> ${thr.count} = red)`} />
          {clinicians.map(c => <ClinicianRow key={c.name} clin={c} field="late" fmt={fmtNum} thr={thr} countColored />)}
          <SectionRow label={`NO SHOWS (> ${thr.count} = red)`} />
          {clinicians.map(c => <ClinicianRow key={c.name} clin={c} field="noshow" fmt={fmtNum} thr={thr} countColored />)}
          <SectionRow label={`CLINICIAN CANCELLATIONS (> ${thr.count} = red)`} />
          {clinicians.map(c => <ClinicianRow key={c.name} clin={c} field="clincxl" fmt={fmtNum} thr={thr} countColored />)}
        </tbody>
      </table>
      </div>
    </div>
  );
}

function SectionRow({ label, color = '#685B72', months }) {
  if (months && months.length) {
    return (
      <tr>
        <td
          className="px-1.5 py-2 font-bold text-white text-[11px] uppercase tracking-widest whitespace-nowrap sticky left-0 z-10 leading-tight"
          style={{ backgroundColor: color }}
        >
          {label}
        </td>
        {months.map(m => (
          <td
            key={m.key}
            className="px-1.5 py-2 text-center text-white text-[10px] font-semibold min-w-[60px] whitespace-nowrap leading-tight"
            style={{ backgroundColor: color }}
          >
            {m.label}
          </td>
        ))}
      </tr>
    );
  }
  return (
    <tr>
      <td
        className="px-1.5 py-2 font-bold text-white text-[11px] uppercase tracking-widest whitespace-nowrap leading-tight"
        style={{ backgroundColor: color }}
        colSpan={99}
      >
        {label}
      </td>
    </tr>
  );
}
function Row({ label, values, fmt, cellColor, indent, bold, rowBg }) {
  return (
    <tr className={rowBg || ''}>
      <td className={`px-1.5 py-1 leading-tight w-px whitespace-nowrap sticky left-0 z-10 ${rowBg || 'bg-white'} ${indent ? 'pl-6' : ''} ${bold ? 'font-semibold' : ''}`}>{label}</td>
      {values.map((v, i) => <td key={i} className={`px-1.5 py-1 leading-tight text-center font-mono whitespace-nowrap ${cellColor ? cellColor(v) : ''} ${bold ? 'font-semibold' : ''}`}>{fmt(v)}</td>)}
    </tr>
  );
}
function ClinicianRow({ clin, field, fmt, thr, rateColored, countColored, colorFrom, nameSuffix = '' }) {
  return (
    <tr>
      <td className="px-1.5 py-1 leading-tight w-px whitespace-nowrap sticky left-0 z-10 bg-white">{clin.name}<span className="text-[10px] text-[#685B72]">{nameSuffix}</span></td>
      {clin[field].map((v, i) => {
        const notPresent = !clin.present[i];
        let cls = '';
        if (notPresent) cls = GRAY;
        else if (rateColored) cls = rateColor(v, thr);
        else if (countColored) cls = countColor(v, thr);
        else if (colorFrom) cls = rateColor(clin[colorFrom][i], thr);
        return <td key={i} className={`px-1.5 py-1 leading-tight text-center font-mono whitespace-nowrap ${cls}`}>{fmt(v)}</td>;
      })}
    </tr>
  );
}

// ────────────────────────────── MAIN PAGE ──────────────────────────────
export default function Performance() {
  const { user } = useAuth();
  const isAdmin = ['admin', 'super_user', 'hr_payroll'].includes(user?.role);
  const isOD = user?.role === 'office_director';
  // super_user + hr_payroll can independently switch the Detailed table's
  // scope (Group / any clinic) without touching the page-level scope. Keeps
  // the hero charts on one view while they drill month-by-month elsewhere.
  const canOverrideDetailedScope = ['super_user', 'hr_payroll'].includes(user?.role);

  const odClinics = useMemo(() => user?.clinics && user.clinics.length ? user.clinics : (user?.clinic ? [user.clinic] : []), [user]);

  // OD forced default: their first clinic. Only Admin sees group.
  const [scope, setScope] = useState(isOD ? (odClinics[0] || 'group') : 'group');
  const [start, setStart] = useState('2026-01');
  const [months, setMonths] = useState(12);
  const [range, setRange] = useState({ from: null, to: null });
  const [report, setReport] = useState(null);
  const [availMonths, setAvailMonths] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  // Detailed table's own scope. Defaults to page scope, follows it when the
  // user changes the top-level scope selector, but can then diverge.
  const [detailedScope, setDetailedScope] = useState(scope);
  const [detailedReport, setDetailedReport] = useState(null);
  // OD-only: parallel group-wide report used for the anonymized org KPI band.
  // Never rendered for admins (they already see full org data in scope=group).
  const [groupReport, setGroupReport] = useState(null);

  const onRangeChange = (r) => {
    setRange(r);
    if (r.from || r.to) {
      const { start: s, months: m } = rangeToMonths(r);
      setStart(s); setMonths(m);
    } else {
      // "All time" — use widest reasonable window
      setStart('2025-01'); setMonths(36);
    }
  };

  const load = useCallback(async () => {
    try {
      const [r, mo] = await Promise.all([
        api.get('/performance/report', { params: { scope, months, start } }),
        api.get('/performance/months'),
      ]);
      setReport(r.data);
      setAvailMonths(mo.data.combined || []);
      if (mo.data.combined?.length && !mo.data.combined.includes(start)) setStart(mo.data.combined[0]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load report');
    }
  }, [scope, months, start]);

  useEffect(() => { load(); }, [load, refreshKey]);

  // Keep detailed-table scope in sync when the page scope changes — but the
  // user can override it afterward using the dropdown inside the table.
  useEffect(() => { setDetailedScope(scope); }, [scope]);

  // Independent fetch for the detailed table when its scope differs from the
  // page scope. When they match we just reuse the main `report` (no extra
  // roundtrip).
  useEffect(() => {
    if (!canOverrideDetailedScope) { setDetailedReport(null); return; }
    if (detailedScope === scope) { setDetailedReport(null); return; }
    let cancelled = false;
    api.get('/performance/report', { params: { scope: detailedScope, months, start } })
      .then(r => { if (!cancelled) setDetailedReport(r.data); })
      .catch(() => { if (!cancelled) setDetailedReport(null); });
    return () => { cancelled = true; };
  }, [canOverrideDetailedScope, detailedScope, scope, months, start, refreshKey]);

  // OD-only side load — the anonymized org KPI band via a dedicated
  // aggregate-only endpoint. Never leaks individual clinician names.
  useEffect(() => {
    if (!isOD) return;
    api.get('/performance/group-snapshot', { params: { months, start } })
      .then(r => setGroupReport(r.data))
      .catch(() => setGroupReport(null));
  }, [isOD, months, start, refreshKey]);

  // Reshape the aggregate response into the {latestLabel, showRate, ...}
  // shape used by the KPI band renderer below.
  const orgKpis = useMemo(() => {
    if (!isOD || !groupReport) return null;
    return {
      latestLabel: groupReport.latest_month || '—',
      showRate: groupReport.show_rate,
      shows: groupReport.sessions_completed || 0,
      onTarget: groupReport.clinicians_on_target || 0,
      atRisk: groupReport.clinicians_at_risk || 0,
      present: groupReport.clinicians_tracked || 0,
    };
  }, [isOD, groupReport]);

  const thr = useMemo(
    () => report?.thresholds || { green: 0.9, yellow: 0.9, count: 8 },
    [report?.thresholds],
  );

  // Scope options depend on role
  const scopeOptions = useMemo(() => {
    if (isAdmin) return [{ v: 'group', l: 'All clinics (Group)' }, ...(report?.clinics || []).map(c => ({ v: c, l: c }))];
    // OD: only their clinics
    return odClinics.map(c => ({ v: c, l: c }));
  }, [isAdmin, odClinics, report]);

  // Compute hero KPIs from the latest populated month
  const heroKpis = useMemo(() => {
    if (!report) return null;
    // find last month with data
    let lastIdx = -1;
    for (let i = report.months.length - 1; i >= 0; i--) {
      if ((report.summary.scheduled[i] ?? 0) > 0) { lastIdx = i; break; }
    }
    if (lastIdx < 0) return { latestLabel: '—' };
    const latestLabel = report.months[lastIdx].label;
    const showRate = report.summary.show_rate[lastIdx];
    const sched = report.summary.scheduled[lastIdx] || 0;
    const shows = report.summary.show[lastIdx] || 0;
    // On-target %: clinicians present that month with vs_target >= green threshold
    let present = 0, onTarget = 0, atRisk = 0;
    for (const c of report.clinicians) {
      if (!c.present[lastIdx]) continue;
      present++;
      const vt = c.vs_target[lastIdx];
      if (vt != null && vt >= thr.green) onTarget++;
      const r = c.rate[lastIdx];
      if ((r != null && r < thr.yellow) || (vt != null && vt < thr.yellow)) atRisk++;
    }
    return { latestLabel, showRate, sched, shows, present, onTarget, atRisk };
  }, [report, thr]);

  // Trend series
  const trendData = useMemo(() => {
    if (!report) return [];
    return report.months.map((m, i) => ({
      label: m.label,
      show_rate: report.summary.show_rate[i] == null ? null : Math.round(report.summary.show_rate[i] * 1000) / 10,
      scheduled: report.summary.scheduled[i] || 0,
      show: report.summary.show[i] || 0,
    }));
  }, [report]);

  // Clinic bar (Group only) from latest populated month
  const clinicBarData = useMemo(() => {
    if (!report?.by_clinic) return [];
    let idx = -1;
    for (let i = report.months.length - 1; i >= 0; i--) if ((report.summary.scheduled[i] ?? 0) > 0) { idx = i; break; }
    if (idx < 0) return [];
    return (report.clinics || []).map(c => ({
      clinic: c,
      rate: report.by_clinic.show_rate[c][idx] == null ? 0 : Math.round(report.by_clinic.show_rate[c][idx] * 1000) / 10,
      completed: report.by_clinic.completed[c][idx] || 0,
    })).sort((a, b) => b.rate - a.rate);
  }, [report]);

  // Per-clinician cards: for each clinician, show latest rate + trend + vs-target
  const clinicianCards = useMemo(() => {
    if (!report) return [];
    let latestIdx = -1;
    for (let i = report.months.length - 1; i >= 0; i--) if ((report.summary.scheduled[i] ?? 0) > 0) { latestIdx = i; break; }
    return report.clinicians.map(c => {
      const present = c.present[latestIdx];
      const rate = c.rate[latestIdx];
      const vsTgt = c.vs_target[latestIdx];
      const avg = c.avg_per_week[latestIdx];
      // Cancel-flag (iter 39): >7 client cancels in the current month for
      // an actively-scheduled clinician. `cancel` already excludes PTO /
      // Approved Leave because those don't map to any STATUS_KEY in
      // performance.py (they never become a `cancel` count).
      const cancels = c.cancel?.[latestIdx];
      const cancelFlag = present && typeof cancels === 'number' && cancels > 7;
      let status = 'off';
      if (present) {
        const key = vsTgt != null ? vsTgt : rate;
        if (key == null) status = 'unknown';
        else if (key >= thr.green) status = 'ok';
        else if (key >= thr.yellow) status = 'warn';
        else status = 'bad';
      }
      return { c, present, rate, vsTgt, avg, status, cancels, cancelFlag };
    });
  }, [report, thr]);

  // Pre-sorted variant for the card grid — memoized so we don't run
  // `.slice().sort()` on every parent re-render (~40 clinicians × render count).
  const sortedClinicianCards = useMemo(
    () => clinicianCards.slice().sort((a, b) => (b.vsTgt ?? -1) - (a.vsTgt ?? -1)),
    [clinicianCards],
  );

  return (
    <div className="space-y-6" data-testid="performance-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl text-[#2D2833] flex items-center gap-2">
            <TrendingUp size={22} className="text-[#685B72]" /> Clinician performance
          </h1>
          <p className="text-sm text-[#685B72] mt-1 max-w-3xl leading-relaxed">
            {isOD
              ? <>Your clinic's performance snapshot. Sessions are attributed to each clinician's majority location per month.</>
              : <>Monthly clinician performance across all clinics. Upload the SimplePractice Attendance + Appointment Status exports each month.</>}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <DateRangePicker value={range} onChange={onRangeChange} defaultPreset="ytd" testid="perf-date-range" />
          {isAdmin && (
            <>
              <Link to="/import" className="text-sm rounded-xl border border-[#BAA9CA] px-3 py-2 hover:bg-[#F4F2F6] flex items-center gap-2" data-testid="link-import">
                <Upload size={14} /> Data Import
              </Link>
              <Link to="/clinicians" className="text-sm rounded-xl border border-[#BAA9CA] px-3 py-2 hover:bg-[#F4F2F6] flex items-center gap-2" data-testid="link-roster">
                <Users size={14} /> Roster & Targets
              </Link>
            </>
          )}
        </div>
      </div>

      {/* Controls */}
      <Card className="p-4 rounded-2xl border-[#E8E5EC] flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <label className="text-xs text-[#685B72] font-semibold">Scope</label>
          <select value={scope} onChange={e => setScope(e.target.value)} className="border rounded-lg px-2 py-1 text-sm bg-white" data-testid="perf-scope">
            {scopeOptions.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-[#685B72] font-semibold">Start month</label>
          <select value={start} onChange={e => setStart(e.target.value)} className="border rounded-lg px-2 py-1 text-sm bg-white" data-testid="perf-start">
            {(availMonths.length ? availMonths : [start]).map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-[#685B72] font-semibold">Window</label>
          <select value={months} onChange={e => setMonths(Number(e.target.value))} className="border rounded-lg px-2 py-1 text-sm bg-white" data-testid="perf-months">
            {[6, 12, 18, 24].map(n => <option key={n} value={n}>{`${n} months`}</option>)}
          </select>
        </div>
      </Card>

      {!report && <div className="text-[#685B72]">Loading…</div>}

      {report && heroKpis && (
        <>
          {/* Group snapshot for ODs (iter 39) — anonymized org-wide KPIs so
              Office Directors can compare their team against the whole org
              without seeing individual names or per-clinic breakdowns. */}
          {isOD && orgKpis && (
            <Card className="rounded-2xl border-[#BAA9CA]/40 bg-[#F7F3FA] p-4" data-testid="od-group-snapshot">
              <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
                <div>
                  <div className="text-[10px] uppercase tracking-widest font-semibold text-[#685B72]">Org-wide snapshot · {orgKpis.latestLabel}</div>
                  <div className="text-xs text-[#685B72] mt-0.5">Broad group stats — how your team compares. Anonymized: no individual names, no per-clinic breakdown.</div>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <HeroTile
                  label="Group show rate"
                  value={fmtPct(orgKpis.showRate)}
                  tone={orgKpis.showRate == null ? 'neutral' : orgKpis.showRate >= thr.green ? 'ok' : orgKpis.showRate >= thr.yellow ? 'warn' : 'bad'}
                  sub="across all clinics"
                  testId="org-show-rate"
                />
                <HeroTile label="Sessions completed" value={fmtNum(orgKpis.shows)} sub="org-wide" tone="accent" testId="org-completed" />
                <HeroTile label="Clinicians on-target" value={`${orgKpis.onTarget}/${orgKpis.present}`} sub="hit ≥ target weekly avg" tone="ok" testId="org-on-target" />
                <HeroTile label="At risk" value={orgKpis.atRisk} sub="< 90% show or target" tone={orgKpis.atRisk === 0 ? 'ok' : 'bad'} testId="org-at-risk" />
              </div>
            </Card>
          )}

          {/* Hero KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="perf-hero">
            <HeroTile
              label={`Show rate · ${heroKpis.latestLabel}`}
              value={fmtPct(heroKpis.showRate)}
              tone={heroKpis.showRate == null ? 'neutral' : heroKpis.showRate >= thr.green ? 'ok' : heroKpis.showRate >= thr.yellow ? 'warn' : 'bad'}
              sub={`${fmtNum(heroKpis.shows)} of ${fmtNum(heroKpis.sched)} scheduled`}
              testId="hero-show-rate"
            />
            <HeroTile label="Sessions completed" value={fmtNum(heroKpis.shows)} sub={`in ${heroKpis.latestLabel}`} tone="accent" testId="hero-completed" />
            <HeroTile label="Clinicians on-target" value={`${heroKpis.onTarget}/${heroKpis.present}`} sub="hit ≥ target weekly avg" tone="ok" testId="hero-on-target" />
            <HeroTile label="At risk" value={heroKpis.atRisk} sub="< 90% show or target" tone={heroKpis.atRisk === 0 ? 'ok' : 'bad'} testId="hero-at-risk" />
            <HeroTile label="Total in view" value={heroKpis.present} sub="clinicians tracked this month" tone="neutral" testId="hero-tracked" />
          </div>

          {/* Charts row */}
          <MonthlyAppointmentsBar report={report} scope={scope} months={months} />
          <div className={`grid grid-cols-1 ${report.by_clinic ? 'lg:grid-cols-2' : ''} gap-4`}>
            <Card className="p-4 rounded-2xl border-[#E8E5EC]" data-testid="chart-show-rate-trend">
              <div className="flex items-center justify-between mb-1">
                <div>
                  <div className="font-serif text-lg text-[#2D2833]">Show rate trend</div>
                  <div className="text-xs text-[#685B72]">{scope === 'group' ? 'All clinics' : scope} · last {months} months</div>
                </div>
                <div className="flex items-center gap-3 text-[10px] text-[#685B72]">
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: OK_DOT }} /> ≥90%</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: WARN_DOT }} /> 85–90</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: BAD_DOT }} /> &lt;85</span>
                </div>
              </div>
              <p className="text-xs text-[#685B72] mb-3" data-testid="chart-show-rate-trend-desc">
                This chart tracks the overall {scope === 'group' ? 'group' : scope} show-rate month-by-month so you can spot dips and lifts across the {months}-month window.
              </p>
              <div style={{ width: '100%', height: 240, minHeight: 240 }}>
                <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
                  <LineChart data={trendData} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F1EDF5" />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#685B72' }} />
                    <YAxis domain={[Math.min(60, thr.yellow * 100 - 5), 100]} tick={{ fontSize: 11, fill: '#685B72' }} unit="%" />
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #E8E5EC' }} />
                    <Line type="monotone" dataKey="show_rate" name="Show rate %" stroke="#685B72" strokeWidth={2.5} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Card>

            {report.by_clinic && (
              <Card className="p-4 rounded-2xl border-[#E8E5EC]" data-testid="chart-by-clinic">
                <div className="mb-2">
                  <div className="font-serif text-lg text-[#2D2833]">Show rate by clinic · {heroKpis.latestLabel}</div>
                  <div className="text-xs text-[#685B72]">Ranked — where marketing/coaching lift is needed</div>
                </div>
                <div style={{ width: '100%', height: 240, minHeight: 240 }}>
                  <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
                    <BarChart data={clinicBarData} layout="vertical" margin={{ top: 8, right: 32, left: 8, bottom: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#F1EDF5" horizontal={false} />
                      <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11, fill: '#685B72' }} unit="%" />
                      <YAxis type="category" dataKey="clinic" width={90} tick={{ fontSize: 11, fill: '#2D2833' }} />
                      <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #E8E5EC' }} />
                      <Bar dataKey="rate" name="Show rate %" radius={[4, 4, 4, 4]}>
                        {clinicBarData.map((entry, i) => (
                          <ReCell key={i} fill={entry.rate >= thr.green * 100 ? OK_DOT : entry.rate >= thr.yellow * 100 ? WARN_DOT : BAD_DOT} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Card>
            )}
          </div>

          {/* Per-clinician cards */}
          <Card className="p-4 rounded-2xl border-[#E8E5EC]" data-testid="clinician-cards">
            <div className="flex items-baseline justify-between mb-3">
              <div>
                <div className="font-serif text-lg text-[#2D2833]">Clinicians — {heroKpis.latestLabel}</div>
                <div className="text-xs text-[#685B72]">Sorted by % of weekly target (highest first). Sparkline = show rate trend across the window.</div>
              </div>
              <div className="flex items-center gap-3 text-[10px] text-[#685B72]">
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: OK_DOT }} /> On target</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: WARN_DOT }} /> Watch</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: BAD_DOT }} /> At risk</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: OFF_DOT }} /> Not this month</span>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {sortedClinicianCards.map(({ c, present, rate, vsTgt, avg, status, cancels, cancelFlag }) => {
                  const border = cancelFlag ? 'border-[#8A3A3A]/60'
                    : status === 'ok' ? 'border-[#839E97]/40'
                    : status === 'warn' ? 'border-[#B08A3A]/40'
                    : status === 'bad' ? 'border-[#8A3A3A]/40'
                    : 'border-[#E8E5EC]';
                  return (
                    <div key={c.name} className={`rounded-xl border-2 ${border} p-3 bg-white`} data-testid={`card-clinician-${c.name.replace(/\W+/g, '_')}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-sm text-[#2D2833] truncate flex items-center gap-1.5" title={c.name}>
                            <span className="truncate">{c.name}</span>
                            {cancelFlag && (
                              <span
                                className="inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded-full bg-[#8A3A3A]/15 text-[#8A3A3A] font-semibold border border-[#8A3A3A]/40 whitespace-nowrap"
                                title="More than 7 client cancels this month (PTO / Approved Leave excluded)"
                                data-testid={`cancel-flag-${c.name.replace(/\W+/g, '_')}`}
                              >
                                <ShieldAlert size={9} /> {cancels} cxls
                              </span>
                            )}
                          </div>
                          <div className="text-[10px] text-[#685B72] mt-0.5">
                            {c.target ? `Target ${c.target}/wk` : 'No target set'}
                            {!present && ' · not with us this month'}
                          </div>
                        </div>
                        <TrendSparkline data={c.rate} thr={thr} />
                      </div>
                      <div className="grid grid-cols-3 gap-2 mt-2">
                        <div>
                          <div className="text-[9px] uppercase text-[#685B72] tracking-widest">Show</div>
                          <div className={`text-sm font-mono px-1.5 py-0.5 rounded ${present ? rateColor(rate, thr) : GRAY}`}>{fmtPct(rate)}</div>
                        </div>
                        <div>
                          <div className="text-[9px] uppercase text-[#685B72] tracking-widest">Avg/wk</div>
                          <div className={`text-sm font-mono px-1.5 py-0.5 rounded ${present ? rateColor(vsTgt, thr) : GRAY}`}>{fmtFloat(avg)}</div>
                        </div>
                        <div>
                          <div className="text-[9px] uppercase text-[#685B72] tracking-widest">% target</div>
                          <div className={`text-sm font-mono px-1.5 py-0.5 rounded ${present ? rateColor(vsTgt, thr) : GRAY}`}>{fmtPct(vsTgt)}</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              {clinicianCards.length === 0 && (
                <div className="col-span-full p-6 text-center text-[#685B72] text-sm">
                  No clinicians in this scope yet. Upload monthly attendance data to populate.
                </div>
              )}
            </div>
          </Card>

          {/* Detailed table — collapsible. For ODs we hide the by-clinic
              breakdown rows so they don't see other clinics' numbers side-by-side.
              super_user + hr_payroll get an inline scope dropdown so they can
              drill this table independently of the page-level scope. */}
          <DetailedTable
            report={detailedReport || report}
            scope={detailedScope}
            thr={thr}
            hideByClinic={isOD}
            scopeSelector={canOverrideDetailedScope && scopeOptions.length > 1 ? (
              <div className="flex items-center gap-1.5" data-testid="detailed-scope-wrap">
                <label className="text-[10px] uppercase tracking-widest text-[#685B72] font-semibold">View</label>
                <select
                  value={detailedScope}
                  onChange={e => setDetailedScope(e.target.value)}
                  className="border rounded-lg px-2 py-1 text-xs bg-white"
                  data-testid="detailed-scope-select"
                >
                  {scopeOptions.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                </select>
              </div>
            ) : null}
          />
        </>
      )}
    </div>
  );
}
